import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Contact: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  // In production this would POST to a backend endpoint or a form service.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const info = [
    { icon: <EmailIcon />, label: 'Email', value: 'contact@chahaltravel.com' },
    { icon: <PhoneIcon />, label: 'Phone', value: '+91 97795-66538' },
    { icon: <LocationOnIcon />, label: 'Address', value: 'Mohali, Chandigarh, Punjab' },
    { icon: <AccessTimeIcon />, label: 'Response time', value: 'Within a few hours on business days' },
  ];

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(135deg,#061b31,#0b3156)', py: { xs: 10, md: 12 }, px: 3, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <Typography variant="h2" fontWeight={800} color="white" sx={{ fontSize: { xs: '2rem', md: '2.8rem' } }}>
            Get in touch
          </Typography>
          <Typography variant="h6" color="rgba(226,232,240,0.7)" maxWidth={440} fontWeight={400}>
            Whether you have a question, feedback, or want a custom plan — we're here to help.
          </Typography>
        </Stack>
      </Box>

      {/* Form + info */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Grid container spacing={5} sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 3, md: 6 } }}>
          {/* Info column */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5" fontWeight={800} mb={1}>Contact info</Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
                  Reach out by email, phone, or through the driver support page after login.
                </Typography>
              </Box>
              {info.map((item) => (
                <Stack key={item.label} direction="row" spacing={1.75} alignItems="flex-start">
                  <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#fff4c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9a6a00', flexShrink: 0, mt: 0.25 }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{item.label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Grid>

          {/* Form */}
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, border: '1px solid rgba(7,24,45,0.08)', boxShadow: '0 16px 40px rgba(7,24,45,0.05)' }}>
              {submitted ? (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Message sent!</Typography>
                  <Typography variant="body2">We'll get back to you at {form.email} shortly.</Typography>
                </Alert>
              ) : (
                <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>
                  <Typography variant="h6" fontWeight={700}>Send us a message</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField name="name" label="Full name" value={form.name} onChange={handleChange} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField name="email" label="Email address" type="email" value={form.email} onChange={handleChange} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField name="phone" label="Phone (optional)" value={form.phone} onChange={handleChange} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField name="message" label="Your message" value={form.message} onChange={handleChange} required multiline rows={5} fullWidth size="small" />
                    </Grid>
                  </Grid>
                  <Button type="submit" variant="contained" color="secondary" size="large" sx={{ fontWeight: 700, alignSelf: 'flex-start', px: 4 }}>
                    Send message
                  </Button>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Contact;
